import React, { useEffect, useState, useCallback } from 'react';
import { Table, Select, Switch, message, Typography, Button, Modal, Form, Input } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import api from '../services/api';
import { ROLE_COLORS } from '../theme';

const currentUsername = () => localStorage.getItem('username');

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  const fetchData = useCallback(() => {
    setLoading(true);
    api.get('/auth/users')
      .then(r => setUsers(r.data))
      .catch(() => message.error('Ошибка загрузки пользователей'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (values: any) => {
    setCreating(true);
    try {
      await api.post('/auth/register', values);
      message.success('Пользователь создан');
      setCreateOpen(false);
      form.resetFields();
      fetchData();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Ошибка создания пользователя');
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = async (userId: number, role: string) => {
    try {
      await api.put(`/auth/users/${userId}/role`, { role });
      message.success('Роль обновлена');
      fetchData();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Ошибка изменения роли');
    }
  };

  const handleStatusChange = async (userId: number, is_active: boolean) => {
    try {
      await api.put(`/auth/users/${userId}/status`, { is_active });
      message.success(is_active ? 'Пользователь активирован' : 'Пользователь заблокирован');
      fetchData();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Ошибка изменения статуса');
    }
  };

  const columns: any[] = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: 'Имя пользователя', dataIndex: 'username', width: 200 },
    { title: 'Email', dataIndex: 'email', width: 240 },
    {
      title: 'Роль', dataIndex: 'role', width: 200,
      render: (role: string, rec: any) => (
        <Select
          value={role}
          style={{ width: 180 }}
          disabled={rec.username === currentUsername()}
          onChange={(v) => handleRoleChange(rec.id, v)}
          options={Object.entries(ROLE_COLORS).map(([value, info]) => ({ value, label: info.label }))}
        />
      ),
    },
    {
      title: 'Активен', dataIndex: 'is_active', width: 100,
      render: (active: boolean, rec: any) => (
        <Switch
          checked={active}
          disabled={rec.username === currentUsername()}
          onChange={(v) => handleStatusChange(rec.id, v)}
        />
      ),
    },
  ];

  return (
    <div>
      <div className="eyebrow">Администрирование</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography.Title level={3} className="page-title" style={{ marginTop: 0 }}>Пользователи</Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          Добавить пользователя
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="small"
      />

      <Modal
        title="Новый пользователь"
        open={createOpen}
        onCancel={() => { setCreateOpen(false); form.resetFields(); }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} onFinish={handleCreate} layout="vertical" initialValues={{ role: 'viewer' }}>
          <Form.Item name="username" label="Имя пользователя" rules={[{ required: true, message: 'Укажите имя пользователя' }]}>
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Укажите корректный email' }]}>
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item name="password" label="Пароль" rules={[{ required: true, min: 6, message: 'Минимум 6 символов' }]}>
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="role" label="Роль" rules={[{ required: true }]}>
            <Select options={Object.entries(ROLE_COLORS).map(([value, info]) => ({ value, label: info.label }))} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={creating} block>Создать</Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UsersPage;
